"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Command as CommandPrimitive } from "cmdk";
import { Check, ChevronsUpDown, X } from "lucide-react";
import * as React from "react";

import { Button, type ButtonProps } from "@src/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@src/shared/components/ui/command";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@src/shared/components/ui/popover";
import { cn } from "@src/shared/lib/utils";

type ComboboxItemValue = unknown;

export type ComboboxInputValueChangeSource =
  | "clear"
  | "close"
  | "input"
  | "select"
  | "sync";

export type ComboboxInputValueChangeMeta = {
  source: ComboboxInputValueChangeSource;
};

type ComboboxBaseProps<Item> = {
  items: readonly Item[];
  children: React.ReactNode;
  closeOnSelect?: boolean;
  itemToStringValue?: (item: Item) => string;
  itemToValue?: (item: Item) => string;
  label?: string;
  resetInputOnClose?: boolean;
  shouldFilter?: boolean;
  disabled?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
};

type ComboboxSingleProps<Item> = ComboboxBaseProps<Item> & {
  multiple?: false;
  value?: Item | null;
  defaultValue?: Item | null;
  onValueChange?: (value: Item | null) => void;
};

type ComboboxMultipleProps<Item> = ComboboxBaseProps<Item> & {
  multiple: true;
  value?: Item[];
  defaultValue?: Item[];
  onValueChange?: (value: Item[]) => void;
};

export type ComboboxProps<Item> =
  | ComboboxSingleProps<Item>
  | ComboboxMultipleProps<Item>;

type ComboboxContextValue<Item> = {
  disabled: boolean;
  inputValue: string;
  items: readonly Item[];
  itemToStringValue: (item: Item) => string;
  itemToValue: (item: Item) => string;
  listboxId: string;
  multiple: boolean;
  open: boolean;
  selectedItems: Item[];
  setInputValue: (
    value: string,
    source?: ComboboxInputValueChangeSource,
  ) => void;
  setOpen: (open: boolean) => void;
  clearValue: () => void;
  isSelected: (item: Item) => boolean;
  registerInputValueChangeHandler: (
    handler:
      | ((value: string, meta: ComboboxInputValueChangeMeta) => void)
      | undefined,
  ) => () => void;
  removeItem: (item: Item) => void;
  selectItem: (item: Item) => void;
};

type ControllableStateOptions<Value> = {
  value: Value | undefined;
  defaultValue: Value;
  onChange?: (value: Value) => void;
};

function defaultItemToStringValue(
  item: ComboboxItemValue,
  objectValueIds: WeakMap<object, string>,
  createObjectValueId: () => string,
): string {
  if (typeof item === "string") {
    return item;
  }

  if (typeof item === "number" || typeof item === "boolean") {
    return String(item);
  }

  if (typeof item === "bigint" || typeof item === "symbol") {
    return String(item);
  }

  if (item && (typeof item === "object" || typeof item === "function")) {
    const existingValueId = objectValueIds.get(item);

    if (existingValueId) {
      return existingValueId;
    }

    const valueId = createObjectValueId();
    objectValueIds.set(item, valueId);

    return valueId;
  }

  return "";
}

function useControllableState<Value>({
  value,
  defaultValue,
  onChange,
}: ControllableStateOptions<Value>): [Value, (nextValue: Value) => void] {
  const [uncontrolledValue, setUncontrolledValue] =
    React.useState<Value>(defaultValue);
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : uncontrolledValue;

  const setValue = React.useCallback(
    (nextValue: Value) => {
      if (!isControlled) {
        setUncontrolledValue(nextValue);
      }

      onChange?.(nextValue);
    },
    [isControlled, onChange],
  );

  return [currentValue, setValue];
}

const ComboboxContext =
  React.createContext<ComboboxContextValue<ComboboxItemValue> | null>(null);

function useComboboxContext<Item>(): ComboboxContextValue<Item> {
  const context = React.useContext(ComboboxContext);

  if (!context) {
    throw new Error("Combobox components must be rendered inside Combobox.");
  }

  return context as ComboboxContextValue<Item>;
}

function valuesMatch<Item>(
  itemToValue: (item: Item) => string,
  left: Item,
  right: Item,
): boolean {
  if (Object.is(left, right)) {
    return true;
  }

  return itemToValue(left) === itemToValue(right);
}

function getSelectedItems<Item>(
  value: Item | Item[] | null,
  multiple: boolean,
): Item[] {
  if (multiple) {
    return Array.isArray(value) ? value : [];
  }

  return value == null || Array.isArray(value) ? [] : [value];
}

function getSelectedInputValue<Item>(
  selectedItems: readonly Item[],
  itemToStringValue: (item: Item) => string,
  multiple: boolean,
): string {
  if (multiple) {
    return "";
  }

  return selectedItems[0] ? itemToStringValue(selectedItems[0]) : "";
}

/**
 * A reusable, shadcn-style combobox built from the app's Command, Popover,
 * Button, and Input visual patterns.
 */
export function Combobox<Item>({
  items,
  children,
  closeOnSelect = true,
  itemToStringValue: itemToStringValueProp,
  itemToValue: itemToValueProp,
  disabled = false,
  defaultOpen = false,
  label,
  open: controlledOpen,
  onOpenChange,
  className,
  resetInputOnClose = true,
  shouldFilter = true,
  ...props
}: ComboboxProps<Item>) {
  const multiple = props.multiple === true;
  const listboxId = React.useId();
  const defaultObjectValueIdsRef = React.useRef(new WeakMap<object, string>());
  const defaultObjectValueIdCountRef = React.useRef(0);
  const itemToStringValue = React.useCallback(
    (item: Item) => {
      if (itemToStringValueProp) {
        return itemToStringValueProp(item);
      }

      return defaultItemToStringValue(
        item,
        defaultObjectValueIdsRef.current,
        () => {
          defaultObjectValueIdCountRef.current += 1;
          return `combobox-object-${defaultObjectValueIdCountRef.current}`;
        },
      );
    },
    [itemToStringValueProp],
  );
  const itemToValue = React.useCallback(
    (item: Item) => itemToValueProp?.(item) ?? itemToStringValue(item),
    [itemToStringValue, itemToValueProp],
  );
  const defaultValue = (
    props.defaultValue === undefined
      ? multiple
        ? []
        : null
      : props.defaultValue
  ) as Item | Item[] | null;
  const [selectedValue, setSelectedValue] = useControllableState<
    Item | Item[] | null
  >({
    value: props.value as Item | Item[] | null | undefined,
    defaultValue,
    onChange: props.onValueChange as
      | ((value: Item | Item[] | null) => void)
      | undefined,
  });
  const [open, setOpen] = useControllableState<boolean>({
    value: controlledOpen,
    defaultValue: defaultOpen,
    onChange: onOpenChange,
  });
  const [isMounted, setIsMounted] = React.useState(false);
  const selectedItems = React.useMemo(
    () => getSelectedItems(selectedValue, multiple),
    [multiple, selectedValue],
  );
  const selectedInputValue = React.useMemo(
    () => getSelectedInputValue(selectedItems, itemToStringValue, multiple),
    [itemToStringValue, multiple, selectedItems],
  );
  const [inputValue, setInputValueState] = React.useState(selectedInputValue);
  const inputValueRef = React.useRef(selectedInputValue);
  const inputValueChangeHandlerRef = React.useRef<
    ((value: string, meta: ComboboxInputValueChangeMeta) => void) | undefined
  >(undefined);

  const registerInputValueChangeHandler = React.useCallback(
    (
      handler:
        | ((value: string, meta: ComboboxInputValueChangeMeta) => void)
        | undefined,
    ) => {
      inputValueChangeHandlerRef.current = handler;

      return () => {
        if (inputValueChangeHandlerRef.current === handler) {
          inputValueChangeHandlerRef.current = undefined;
        }
      };
    },
    [],
  );

  const setInputValue = React.useCallback(
    (nextValue: string, source: ComboboxInputValueChangeSource = "sync") => {
      if (inputValueRef.current === nextValue) {
        return;
      }

      inputValueRef.current = nextValue;
      setInputValueState(nextValue);
      inputValueChangeHandlerRef.current?.(nextValue, { source });
    },
    [],
  );

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  React.useEffect(() => {
    setInputValue(selectedInputValue, "sync");
  }, [selectedInputValue]);

  React.useEffect(() => {
    if (!open && resetInputOnClose) {
      setInputValue(selectedInputValue, "close");
    }
  }, [open, resetInputOnClose, selectedInputValue, setInputValue]);

  const isSelected = React.useCallback(
    (item: Item) =>
      selectedItems.some((selectedItem) =>
        valuesMatch(itemToValue, selectedItem, item),
      ),
    [itemToValue, selectedItems],
  );

  const updateValue = React.useCallback(
    (nextValue: Item | Item[] | null) => {
      setSelectedValue(nextValue);
    },
    [setSelectedValue],
  );

  const removeItem = React.useCallback(
    (item: Item) => {
      if (disabled) {
        return;
      }

      if (multiple) {
        updateValue(
          selectedItems.filter(
            (selectedItem) => !valuesMatch(itemToValue, selectedItem, item),
          ),
        );
        return;
      }

      updateValue(null);
      setInputValue("", "clear");
    },
    [
      disabled,
      itemToValue,
      multiple,
      selectedItems,
      updateValue,
      setInputValue,
    ],
  );

  const clearValue = React.useCallback(() => {
    if (disabled) {
      return;
    }

    updateValue(multiple ? [] : null);
    setInputValue("", "clear");
  }, [disabled, multiple, updateValue]);

  const selectItem = React.useCallback(
    (item: Item) => {
      if (disabled) {
        return;
      }

      if (multiple) {
        const nextValue = isSelected(item)
          ? selectedItems.filter(
              (selectedItem) => !valuesMatch(itemToValue, selectedItem, item),
            )
          : [...selectedItems, item];

        updateValue(nextValue);
        setInputValue("", "select");
        return;
      }

      updateValue(item);
      setInputValue(itemToStringValue(item), "select");
      if (closeOnSelect) {
        setOpen(false);
      }
    },
    [
      closeOnSelect,
      disabled,
      isSelected,
      itemToStringValue,
      itemToValue,
      multiple,
      selectedItems,
      setOpen,
      updateValue,
    ],
  );

  const isPopoverOpen = !disabled && isMounted && open;

  const context = React.useMemo<ComboboxContextValue<Item>>(
    () => ({
      disabled,
      inputValue,
      items,
      itemToStringValue,
      itemToValue,
      listboxId,
      multiple,
      open: isPopoverOpen,
      selectedItems,
      setInputValue,
      setOpen,
      clearValue,
      isSelected,
      registerInputValueChangeHandler,
      removeItem,
      selectItem,
    }),
    [
      clearValue,
      disabled,
      inputValue,
      isSelected,
      itemToStringValue,
      itemToValue,
      items,
      isPopoverOpen,
      listboxId,
      multiple,
      registerInputValueChangeHandler,
      removeItem,
      selectItem,
      selectedItems,
      setOpen,
    ],
  );

  return (
    <ComboboxContext.Provider
      value={context as ComboboxContextValue<ComboboxItemValue>}
    >
      <Command
        className={cn("overflow-visible bg-transparent", className)}
        label={label}
        shouldFilter={shouldFilter}
      >
        <Popover
          open={isPopoverOpen}
          onOpenChange={(nextOpen) => setOpen(disabled ? false : nextOpen)}
        >
          {children}
        </Popover>
      </Command>
    </ComboboxContext.Provider>
  );
}

export type ComboboxInputProps = Omit<
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>,
  "onValueChange" | "value"
> & {
  asAnchor?: boolean;
  onValueChange?: (value: string, meta: ComboboxInputValueChangeMeta) => void;
  showClear?: boolean;
};

export const ComboboxAnchor = PopoverAnchor;

export const ComboboxInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  ComboboxInputProps
>(
  (
    {
      asAnchor = true,
      className,
      disabled,
      onFocus,
      onValueChange,
      placeholder,
      showClear = false,
      ...props
    },
    ref,
  ) => {
    const {
      clearValue,
      disabled: comboboxDisabled,
      inputValue,
      listboxId,
      open,
      registerInputValueChangeHandler,
      setInputValue,
      setOpen,
    } = useComboboxContext();
    const isDisabled = disabled || comboboxDisabled;
    const showClearButton = showClear && inputValue.length > 0 && !isDisabled;

    React.useEffect(
      () => registerInputValueChangeHandler(onValueChange),
      [onValueChange, registerInputValueChangeHandler],
    );

    const input = (
      <div className="relative w-full" data-combobox-anchor="">
        <CommandPrimitive.Input
          aria-autocomplete="list"
          aria-controls={open ? listboxId : undefined}
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md-stitch:text-sm",
            showClearButton ? "pr-9" : null,
            className,
          )}
          disabled={isDisabled}
          onFocus={(event) => {
            setOpen(true);
            onFocus?.(event);
          }}
          onValueChange={(nextValue) => {
            setInputValue(nextValue, "input");
            setOpen(true);
          }}
          placeholder={placeholder}
          ref={ref}
          role="combobox"
          value={inputValue}
          {...props}
        />
        {showClearButton ? (
          <Button
            aria-label="Clear selection"
            className="absolute top-1/2 right-1 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => {
              clearValue();
              setInputValue("", "clear");
            }}
            size="icon"
            type="button"
            variant="ghost"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    );

    return asAnchor ? <PopoverAnchor asChild>{input}</PopoverAnchor> : input;
  },
);
ComboboxInput.displayName = "ComboboxInput";

export type ComboboxTriggerProps = Omit<ButtonProps, "children" | "value"> & {
  placeholder?: React.ReactNode;
  renderValue?: (items: ComboboxItemValue[]) => React.ReactNode;
};

export const ComboboxTrigger = React.forwardRef<
  HTMLButtonElement,
  ComboboxTriggerProps
>(
  (
    { className, disabled, placeholder = "Select item", renderValue, ...props },
    ref,
  ) => {
    const {
      disabled: comboboxDisabled,
      itemToStringValue,
      listboxId,
      multiple,
      open,
      selectedItems,
    } = useComboboxContext();
    const isDisabled = disabled || comboboxDisabled;
    const hasValue = selectedItems.length > 0;
    const value = renderValue
      ? renderValue(selectedItems)
      : multiple
        ? selectedItems.map(itemToStringValue).join(", ")
        : selectedItems[0]
          ? itemToStringValue(selectedItems[0])
          : null;

    return (
      <PopoverTrigger asChild>
        <Button
          aria-controls={open ? listboxId : undefined}
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !hasValue ? "text-muted-foreground" : null,
            className,
          )}
          disabled={isDisabled}
          ref={ref}
          role="combobox"
          type="button"
          variant="outline"
          {...props}
        >
          <span className="truncate">{hasValue ? value : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
    );
  },
);
ComboboxTrigger.displayName = "ComboboxTrigger";

export type ComboboxContentProps = React.ComponentPropsWithoutRef<
  typeof PopoverContent
> & {
  portalled?: boolean;
};

export const ComboboxContent = React.forwardRef<
  React.ElementRef<typeof PopoverContent>,
  ComboboxContentProps
>(
  (
    {
      align = "start",
      className,
      onCloseAutoFocus,
      onInteractOutside,
      onOpenAutoFocus,
      portalled = true,
      sideOffset = 4,
      ...props
    },
    ref,
  ) => {
    const handleInteractOutside: ComboboxContentProps["onInteractOutside"] = (
      event,
    ) => {
      if (
        event.target instanceof Element &&
        event.target.closest("[data-combobox-anchor]")
      ) {
        event.preventDefault();
      }

      onInteractOutside?.(event);
    };
    const handleOpenAutoFocus: ComboboxContentProps["onOpenAutoFocus"] = (
      event,
    ) => {
      event.preventDefault();
      onOpenAutoFocus?.(event);
    };
    const handleCloseAutoFocus: ComboboxContentProps["onCloseAutoFocus"] = (
      event,
    ) => {
      event.preventDefault();
      onCloseAutoFocus?.(event);
    };
    const contentClassName = cn(
      "w-[--radix-popover-trigger-width] p-0",
      className,
    );

    if (!portalled) {
      return (
        <PopoverPrimitive.Content
          align={align}
          className={cn(
            "z-50 w-72 origin-[--radix-popover-content-transform-origin] rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            contentClassName,
          )}
          onInteractOutside={handleInteractOutside}
          onCloseAutoFocus={handleCloseAutoFocus}
          onOpenAutoFocus={handleOpenAutoFocus}
          ref={ref}
          sideOffset={sideOffset}
          {...props}
        />
      );
    }

    return (
      <PopoverContent
        align={align}
        className={contentClassName}
        onInteractOutside={handleInteractOutside}
        onCloseAutoFocus={handleCloseAutoFocus}
        onOpenAutoFocus={handleOpenAutoFocus}
        ref={ref}
        sideOffset={sideOffset}
        {...props}
      />
    );
  },
);
ComboboxContent.displayName = "ComboboxContent";

export const ComboboxEmpty = CommandEmpty;

export type ComboboxListProps<Item> = Omit<
  React.ComponentPropsWithoutRef<typeof CommandList>,
  "children"
> & {
  items?: readonly Item[];
  children: React.ReactNode | ((item: Item) => React.ReactNode);
};

export function ComboboxList<Item>({
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  children,
  className,
  items,
  ...props
}: ComboboxListProps<Item>) {
  const context = useComboboxContext<Item>();
  const listItems = items ?? context.items;
  const generatedLabelId = React.useId();
  const labelId = ariaLabel ? generatedLabelId : ariaLabelledBy;

  return (
    <>
      {ariaLabel ? (
        <span className="sr-only" id={generatedLabelId}>
          {ariaLabel}
        </span>
      ) : null}
      <CommandList
        aria-labelledby={labelId}
        className={cn("max-h-64", className)}
        id={context.listboxId}
        {...props}
      >
        {typeof children === "function"
          ? listItems.map((item) => children(item))
          : children}
      </CommandList>
    </>
  );
}

export type ComboboxCollectionProps<Item> = {
  items?: readonly Item[];
  children: (item: Item) => React.ReactNode;
};

export function ComboboxCollection<Item>({
  items,
  children,
}: ComboboxCollectionProps<Item>) {
  const context = useComboboxContext<Item>();
  const collectionItems = items ?? context.items;

  return <>{collectionItems.map((item) => children(item))}</>;
}

export type ComboboxItemProps<Item> = Omit<
  React.ComponentPropsWithoutRef<typeof CommandItem>,
  "onSelect" | "value"
> & {
  value: Item;
  hideIndicator?: boolean;
  onSelect?: (value: Item) => void;
};

export function ComboboxItem<Item>({
  children,
  className,
  hideIndicator = false,
  onSelect,
  value,
  ...props
}: ComboboxItemProps<Item>) {
  const context = useComboboxContext<Item>();
  const selected = context.isSelected(value);

  return (
    <CommandItem
      aria-selected={selected}
      className={cn("gap-2", selected ? "font-medium" : null, className)}
      data-combobox-selected={selected ? "true" : undefined}
      onSelect={() => {
        context.selectItem(value);
        onSelect?.(value);
      }}
      role="option"
      value={context.itemToValue(value)}
      {...props}
    >
      {hideIndicator ? null : (
        <Check
          aria-hidden="true"
          className={cn("h-4 w-4", selected ? "opacity-100" : "opacity-0")}
        />
      )}
      {children}
    </CommandItem>
  );
}

export const ComboboxGroup = CommandGroup;
export const ComboboxSeparator = CommandSeparator;

export type ComboboxLabelProps = React.HTMLAttributes<HTMLDivElement>;

export const ComboboxLabel = React.forwardRef<
  HTMLDivElement,
  ComboboxLabelProps
>(({ className, ...props }, ref) => (
  <div
    className={cn(
      "px-2 py-1.5 text-muted-foreground text-xs font-medium",
      className,
    )}
    ref={ref}
    {...props}
  />
));
ComboboxLabel.displayName = "ComboboxLabel";

export type ComboboxValueProps<Item> = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  children?:
    | React.ReactNode
    | ((value: Item | Item[] | null) => React.ReactNode);
  placeholder?: React.ReactNode;
};

export function ComboboxValue<Item>({
  children,
  className,
  placeholder = null,
  ...props
}: ComboboxValueProps<Item>) {
  const { itemToStringValue, multiple, selectedItems } =
    useComboboxContext<Item>();
  const value = multiple ? selectedItems : (selectedItems[0] ?? null);
  const fallbackValue =
    selectedItems.length > 0
      ? selectedItems.map(itemToStringValue).join(", ")
      : placeholder;

  return (
    <div className={cn("flex min-w-0 flex-wrap gap-1", className)} {...props}>
      {typeof children === "function"
        ? children(value)
        : (children ?? fallbackValue)}
    </div>
  );
}

export type ComboboxChipsProps = React.HTMLAttributes<HTMLDivElement>;

export const ComboboxChips = React.forwardRef<
  HTMLDivElement,
  ComboboxChipsProps
>(({ children, className, onClick, ...props }, ref) => {
  const { disabled, setOpen } = useComboboxContext();

  return (
    <PopoverAnchor asChild>
      <div
        aria-disabled={disabled}
        className={cn(
          "flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-2 py-1 shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring",
          disabled ? "cursor-not-allowed opacity-50" : null,
          className,
        )}
        data-combobox-anchor=""
        onClick={(event) => {
          if (!disabled) {
            setOpen(true);
          }

          onClick?.(event);
        }}
        ref={ref}
        {...props}
      >
        {children}
      </div>
    </PopoverAnchor>
  );
});
ComboboxChips.displayName = "ComboboxChips";

export type ComboboxChipsInputProps = Omit<
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>,
  "onValueChange" | "value"
> & {
  onValueChange?: (value: string, meta: ComboboxInputValueChangeMeta) => void;
};

export const ComboboxChipsInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  ComboboxChipsInputProps
>(
  (
    { className, disabled, onFocus, onKeyDown, onValueChange, ...props },
    ref,
  ) => {
    const {
      disabled: comboboxDisabled,
      inputValue,
      selectedItems,
      setInputValue,
      setOpen,
      removeItem,
    } = useComboboxContext();
    const isDisabled = disabled || comboboxDisabled;

    return (
      <CommandPrimitive.Input
        className={cn(
          "min-w-24 flex-1 border-0 bg-transparent px-1 py-1 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed",
          className,
        )}
        disabled={isDisabled}
        onFocus={(event) => {
          setOpen(true);
          onFocus?.(event);
        }}
        onKeyDown={(event) => {
          if (
            event.key === "Backspace" &&
            inputValue.length === 0 &&
            selectedItems.length > 0
          ) {
            removeItem(selectedItems[selectedItems.length - 1]);
          }

          onKeyDown?.(event);
        }}
        onValueChange={(nextValue) => {
          setInputValue(nextValue, "input");
          setOpen(true);
          onValueChange?.(nextValue, { source: "input" });
        }}
        ref={ref}
        value={inputValue}
        {...props}
      />
    );
  },
);
ComboboxChipsInput.displayName = "ComboboxChipsInput";

export type ComboboxChipProps<Item> = React.HTMLAttributes<HTMLSpanElement> & {
  value?: Item;
  removable?: boolean;
};

export function ComboboxChip<Item>({
  children,
  className,
  onClick,
  removable = true,
  value,
  ...props
}: ComboboxChipProps<Item>) {
  const context = useComboboxContext<Item>();
  const resolvedValue =
    value ??
    (typeof children === "string"
      ? context.items.find(
          (item) => context.itemToStringValue(item) === children,
        )
      : undefined);

  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-secondary-foreground text-xs",
        className,
      )}
      onClick={onClick}
      {...props}
    >
      <span className="truncate">{children}</span>
      {removable && resolvedValue !== undefined ? (
        <button
          aria-label={`Remove ${context.itemToStringValue(resolvedValue)}`}
          className="rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={(event) => {
            event.stopPropagation();
            context.removeItem(resolvedValue);
          }}
          type="button"
        >
          <X aria-hidden="true" className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  );
}

export type ComboboxClearProps = ButtonProps;

export const ComboboxClear = React.forwardRef<
  HTMLButtonElement,
  ComboboxClearProps
>(({ children, onClick, ...props }, ref) => {
  const { clearValue } = useComboboxContext();

  return (
    <Button
      onClick={(event) => {
        clearValue();
        onClick?.(event);
      }}
      ref={ref}
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <X aria-hidden="true" className="h-4 w-4" />}
    </Button>
  );
});
ComboboxClear.displayName = "ComboboxClear";
